import { useEffect, useState, type ReactNode } from 'react';
import { Logo } from '../components/Logo';
import { FlagFilter } from '../components/FlagWave';
import { AiHeroCanvas } from '../components/AiHeroCanvas';

/**
 * What the hero says.
 *
 * Held as data rather than markup because the admin portal is meant to drive
 * it later (a campaign, a seasonal message, a different image) without a
 * release. The shape below is the contract that endpoint will fill.
 */
export interface HeroContent {
  eyebrow: string;
  headline: string;
  headlineAccent: string;
  supporting: string;
  /** The learning arc, shown as a single line of steps. */
  journey: string[];
  /** The domains SkillXzen teaches. Kept short — this is a login, not a menu. */
  domains: string[];
  /**
   * A photograph for the hero.
   *
   * The default points at a file that is not in the repository. If it is
   * absent the image quietly drops out and the drawn composition carries the
   * panel on its own, so the login never renders a broken image — drop a
   * licensed photograph at that path and it appears with no code change.
   *
   * `alt` is empty by default on purpose: the hero already states what
   * SkillXzen is in the headline, so the photograph behind it is decorative.
   * A CMS-supplied image should set real alt text describing what it shows.
   */
  image?: { src: string; alt: string; focus?: string };
}

export const DEFAULT_HERO: HeroContent = {
  eyebrow: 'AI skills for engineering and IT',
  headline: 'AI Skills.',
  headlineAccent: 'Industry Ready. Future Ready.',
  supporting:
    'Master the practical AI skills, tools and technologies that engineering and IT professionals are being asked for right now.',
  journey: ['Learn', 'Practice', 'Build', 'Apply', 'Grow'],
  // Six, not eight: eight wrapped to three rows and left one chip stranded on
  // its own line. These six carry the positioning; the catalogue carries the
  // rest.
  domains: [
    'Generative AI',
    'LLMs & RAG',
    'AI Agents',
    'Prompt Engineering',
    'AI-assisted Development',
    'AI-powered QA',
  ],
  image: { src: '/brand/hero-login.png', alt: '', focus: 'center 38%' },
};


/** Three true numbers about the catalogue, read from the public endpoint. */
interface HeroStats {
  courses: number;
  categories: number;
  skills: number;
}

/**
 * Facts, not marketing.
 *
 * The hero had a 300px band of dead air under the mark. Rather than pad it or
 * invent a claim, it now carries three figures taken live from the public
 * catalogue — the same numbers a visitor sees if they click through. If the
 * call fails the row simply does not render, and the layout closes up.
 */
function useHeroStats(): HeroStats | null {
  const [stats, setStats] = useState<HeroStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/v1/catalog/search?page=1&pageSize=1')
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (cancelled || !body) return;
        const facets = body.facets ?? {};
        setStats({
          courses: body.totalItems ?? 0,
          categories: (facets.categories ?? []).length,
          skills: (facets.skills ?? []).length,
        });
      })
      .catch(() => {
        /* The hero stands on its own without them. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return stats;
}

/**
 * The signed-out experience.
 *
 * Two panels that read as one surface: an AI hero that says what SkillXzen is
 * for, and an authentication panel that gets out of the way. Below 960px the
 * hero collapses to a compact branded band rather than shrinking — a hero
 * scaled down to phone width is just a small hero, and stops being a hero.
 */
export function AuthLayout({
  children,
  hero = DEFAULT_HERO,
}: {
  children: ReactNode;
  hero?: HeroContent;
}) {
  // A hero photograph is optional and may simply not be on disk. Tracking the
  // failure rather than letting the browser draw its broken-image glyph is
  // what makes the slot safe to point at a file that has not been supplied yet.
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = hero.image && !photoFailed ? hero.image : null;
  const stats = useHeroStats();

  return (
    <div className="auth">
      <section
        className={`aihero${photo ? ' aihero--layered' : ''}`}
        aria-labelledby="sx-hero-headline"
      >
        {/* With a photograph behind it the canvas becomes the motion layer:
            the image cannot move, and its pulsing network can. Without one it
            is the scene itself. */}
        <AiHeroCanvas />
        <FlagFilter />

        {photo ? (
          <img
            className="aihero__photo"
            src={photo.src}
            alt={photo.alt}
            aria-hidden={photo.alt === '' ? true : undefined}
            onError={() => setPhotoFailed(true)}
            style={photo.focus ? { objectPosition: photo.focus } : undefined}
          />
        ) : null}

        <div className="aihero__sweep" aria-hidden="true" />
        <div className="aihero__motes" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <span className="aihero__mote" key={index} />
          ))}
        </div>

        {/* A wash over whatever sits beneath, so the type always has contrast
            whether the layer behind it is the drawn scene or a photograph. */}
        <div className="aihero__wash" aria-hidden="true" />

        <div className="aihero__inner">
          <div className="aihero__brand">
            {/* The on-dark lockup sits straight on the navy — no plate, no
                card — in the brand's own colours, lifted to read on dark. */}
            <span className="sx-flag aihero__flag">
              <Logo variant="login" tone="onDark" />
            </span>
          </div>

          <div className="aihero__copy">
            <p className="aihero__eyebrow">{hero.eyebrow}</p>
            <h1 className="aihero__headline" id="sx-hero-headline">
              {hero.headline} <em>{hero.headlineAccent}</em>
            </h1>
            <p className="aihero__supporting">{hero.supporting}</p>

            {/* Reserved height, so the row appearing after the fetch does not
                shove the journey and chips down the panel. */}
            <dl className="aihero__stats">
              {/* Hidden at zero: "0 courses" on the sign-in page reads as an
                  empty product, not as a catalog between releases. */}
              {stats && stats.courses > 0 ? (
                <>
                  <div className="aihero__stat">
                    <dt>{stats.courses}</dt>
                    <dd>courses</dd>
                  </div>
                  <div className="aihero__stat">
                    <dt>{stats.categories}</dt>
                    <dd>categories</dd>
                  </div>
                  <div className="aihero__stat">
                    <dt>{stats.skills}</dt>
                    <dd>skills tracked</dd>
                  </div>
                </>
              ) : null}
            </dl>

            <ol className="aihero__journey" aria-label="How SkillXzen works">
              {hero.journey.map((step, index) => (
                <li className="aihero__step" key={step}>
                  <span className="aihero__step-dot" aria-hidden="true">
                    {index + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          <div className="aihero__domains" aria-label="What you can learn">
            {hero.domains.map((domain) => (
              <span className="aihero__chip" key={domain}>
                {domain}
              </span>
            ))}
          </div>
        </div>
      </section>

      <main className="authpanel">
        <div className="authpanel__inner">
          {/* Shown only where the hero is not — the mark appears once at any
              width, never twice. */}
          <div className="authpanel__brand">
            <Logo variant="login" tone="auto" />
          </div>
          {children}
        </div>

        <footer className="authpanel__foot">
          <span>© {new Date().getFullYear()} SkillXzen</span>
          <span className="authpanel__foot-sep" aria-hidden="true">
            ·
          </span>
          {/* /pages/… is the web route; /site/pages/… was the API path, which
              the app's catch-all quietly bounced to the catalog. */}
          <a href="/pages/privacy">Privacy</a>
          <span className="authpanel__foot-sep" aria-hidden="true">
            ·
          </span>
          <a href="/pages/terms">Terms</a>
          <span className="authpanel__foot-sep" aria-hidden="true">
            ·
          </span>
          {/* Plain text, not a link: it identifies the build for whoever is
              looking at the screen, and there is nowhere useful to go. */}
          <span className="authpanel__version">v{__APP_VERSION__}</span>
        </footer>
      </main>
    </div>
  );
}
